import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Textarea } from '@cortex/web-design-system/components/ui/Textarea';

export interface BdNotApplicableFormProps {
  /** 節の名前（入力欄の読み上げに使う） */
  title: string;
  /** 保存している最中か */
  saving: boolean;
  /** 理由を付けて「該当なし」で保存する */
  onSubmit: (reason: string) => void;
  onCancel: () => void;
}

/**
 * 「該当なし」にする理由を書いてから保存する欄。
 *
 * 理由が無いと、確かめて該当なしにしたのか、書くのを飛ばしたのかをレビュアーが区別できない。
 * 空・長すぎるかの判定は API が持つ（MCP と同じ規則）ので、画面は空のまま送らないことだけを見る。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計の任意の節を「該当なし」にする理由を書かせてから保存する
 * @graph-connects none
 */
export function BdNotApplicableForm({
  title,
  saving,
  onSubmit,
  onCancel,
}: BdNotApplicableFormProps) {
  const { t } = useTranslation();
  const id = useId();
  const [reason, setReason] = useState('');
  const blank = reason.trim() === '';

  return (
    <form
      className="space-y-2 rounded-xl border border-[var(--color-border)] px-4 py-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!blank) onSubmit(reason);
      }}
    >
      <label htmlFor={id} className="block text-sm font-medium text-[var(--color-text-primary)]">
        {t('bd.notApplicableForm.label')}
      </label>
      <Textarea
        id={id}
        rows={2}
        className="px-3 py-1.5"
        aria-label={t('bd.notApplicableForm.aria', { title })}
        aria-describedby={`${id}-hint`}
        value={reason}
        onChange={(e) => {
          setReason(e.target.value);
        }}
      />
      <p id={`${id}-hint`} className="text-xs text-[var(--color-text-muted)]">
        {t('bd.notApplicableForm.hint')}
      </p>
      <div className="flex flex-wrap gap-2">
        <Button type="submit" size="sm" loading={saving} disabled={saving || blank}>
          {t('bd.notApplicableForm.save')}
        </Button>
        <Button type="button" size="sm" variant="ghost" disabled={saving} onClick={onCancel}>
          {t('bd.notApplicableForm.cancel')}
        </Button>
      </div>
    </form>
  );
}
